import axios from '../../../api/axios';

export const CONTRACTORS_ENDPOINTS = {
  BASE: '/v2/contractor',
  CATEGORIES: '/v2/contractor/categories',
};

/**
 * Centralized API error handler
 * @param {unknown} error
 * @param {string} fallbackMessage
 * @throws {Error}
 */
const handleApiError = (error, fallbackMessage) => {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage;

  console.error('[Contractors API]', message, error);
  throw new Error(message);
};

/**
 * @typedef {Object} ContractorPayload
 * @property {string} company_name
 * @property {string} category
 * @property {string} [description]
 * @property {Array<{ name?: string; phone: string; email?: string }>} [representatives]
 * @property {Array<{ property_id: string; building_name: string }>} [properties]
 */

/**
 * @typedef {Object} ContractorListResponse
 * @property {boolean} success
 * @property {Array<Object>} data
 * @property {{ total: number; page: number; limit: number; pages: number }} pagination
 */

/**
 * @typedef {Object} ContractorSingleResponse
 * @property {boolean} success
 * @property {Object} data
 */

/**
 * Create a new contractor
 * @param {ContractorPayload} payload - Contractor creation payload
 * @returns {Promise<ContractorSingleResponse>} Created contractor response
 */
export const createContractor = async (payload) => {
  try {
    const response = await axios.post(CONTRACTORS_ENDPOINTS.BASE, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to create contractor');
  }
};

/**
 * Update an existing contractor
 * @param {string} id - Contractor MongoDB _id (hex string)
 * @param {Partial<ContractorPayload>} payload - Fields to update
 * @returns {Promise<ContractorSingleResponse>} Updated contractor
 */
export const updateContractor = async (id, payload) => {
  try {
    const url = `${CONTRACTORS_ENDPOINTS.BASE}/${id}`;
    const response = await axios.patch(url, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update contractor');
  }
};

/**
 * Delete a contractor
 * @param {string} id - Contractor _id
 * @returns {Promise<{ success: boolean; message: string }>} Deletion result
 */
export const deleteContractor = async (id) => {
  try {
    const url = `${CONTRACTORS_ENDPOINTS.BASE}/${id}`;
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to delete contractor');
  }
};

/**
 * Get all contractors (with optional filters & pagination)
 * @param {Object} [params={}] Query parameters
 * @param {string} [params.category] - Filter by category (case-insensitive partial match)
 * @param {string} [params.propertyId] - Filter contractors assigned to this property
 * @param {string} [params.search] - Full-text search across name, category, description, etc.
 * @param {number} [params.limit=50]
 * @param {number} [params.page=1]
 * @returns {Promise<ContractorListResponse>}
 */
export const getAllContractors = async (params = {}) => {
  try {
    const response = await axios.get(CONTRACTORS_ENDPOINTS.BASE, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch contractors');
  }
};

/**
 * Get a single contractor by ID
 * @param {string} id - Contractor _id
 * @returns {Promise<ContractorSingleResponse>} Contractor object
 */
export const getContractorById = async (id) => {
  try {
    const url = `${CONTRACTORS_ENDPOINTS.BASE}/${id}`;
    const response = await axios.get(url);
    // Most common patterns: { success: true, data: {...} }
    return response.data?.data ?? response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch contractor');
  }
};

/**
 * Get all unique contractor categories
 * @returns {Promise<{ success: boolean; data: string[] }>} Sorted array of categories
 */
export const getContractorCategories = async () => {
  try {
    const response = await axios.get(CONTRACTORS_ENDPOINTS.CATEGORIES);
    return response.data?.data ?? response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch contractor categories');
  }
};